import React, { useMemo, useState } from 'react';
import { Search, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const Score = ({ value, testId }) => (
  <span className="font-mono text-xs" data-testid={testId}>{value.completed}/{value.total}</span>
);

export const ReadinessImmobiliTab = ({ properties = [] }) => {
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('blocked');
  const filtered = useMemo(() => properties.filter((property) => {
    const matches = `${property.property_code || ''} ${property.name || ''}`.toLowerCase().includes(search.toLowerCase());
    if (!matches) return false;
    if (mode === 'ready') return property.autonomy_ready;
    if (mode === 'media') return property.remote_photo_fallbacks > 0;
    return !property.autonomy_ready;
  }), [properties, search, mode]);

  return (
    <section className="space-y-5" data-testid="readiness-properties-tab-content">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca codice o immobile" className="pl-9" data-testid="property-readiness-search-input" />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="property-readiness-filter-group">
          {[
            ['blocked', 'Da completare'], ['ready', 'Pronti'], ['media', 'Foto legacy'],
          ].map(([value, label]) => (
            <Button key={value} size="sm" variant={mode === value ? 'default' : 'outline'} onClick={() => setMode(value)} data-testid={`property-filter-${value}-button`}>{label}</Button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <Table data-testid="property-readiness-table">
          <TableHeader><TableRow>
            <TableHead>Immobile</TableHead><TableHead>Core</TableHead><TableHead>Legale</TableHead><TableHead>Proprietario</TableHead><TableHead>Vetrina</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Scheda</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.slice(0, 150).map((property, index) => (
              <TableRow key={property.id} data-testid={`property-readiness-row-${index}`}>
                <TableCell><p className="font-semibold text-slate-900">{property.property_code || 'Senza codice'}</p><p className="max-w-64 truncate text-xs text-slate-500">{property.name}</p></TableCell>
                <TableCell><Score value={property.core} testId={`property-core-score-${index}`} /></TableCell>
                <TableCell><Score value={property.legal} testId={`property-legal-score-${index}`} /></TableCell>
                <TableCell><Score value={property.owner} testId={`property-owner-score-${index}`} /></TableCell>
                <TableCell><Score value={property.listing} testId={`property-listing-score-${index}`} /></TableCell>
                <TableCell>
                  {property.autonomy_ready ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="mr-1 h-3 w-3" />Pronto</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800"><AlertTriangle className="mr-1 h-3 w-3" />{property.blockers.length} gap</Badge>}
                  {property.remote_photo_fallbacks > 0 && <p className="mt-1 text-xs text-red-700" data-testid={`property-photo-fallbacks-${index}`}>{property.remote_photo_fallbacks} foto legacy</p>}
                </TableCell>
                <TableCell className="text-right"><Button asChild size="icon" variant="ghost" data-testid={`property-open-sheet-button-${index}`}><Link to={`/properties/${property.id}/scheda`}><ExternalLink className="h-4 w-4" /></Link></Button></TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500" data-testid="property-readiness-empty-state">Nessun immobile per questo filtro</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {filtered.length > 150 && <p className="text-xs text-slate-500" data-testid="property-readiness-result-limit">Mostrati 150 di {filtered.length} risultati</p>}
    </section>
  );
};